/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderObject from "App/geometry/objects/object";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import {mat3, mat4, vec3} from "gl-matrix";
import decompose from "App/utils/text/fonts/path_to_shape_2d";
import {triangulate} from "App/utils/text/fonts/shape_2d_triangulate";
import Factory from "App/geometry/objects_geometry/factory";

export default
class Text extends AbstractGeometry {

    public static TypeName: string = "textGeometry";

    public indicesDesc: VertexDataDescriptor = null;
    public nonIndexedPositionsDesc: VertexDataDescriptor = null;

    constructor(aObject: RenderObject) {
        super(aObject);
    }

    public static create(aObject: RenderObject) {
        return new Text(aObject);
    }

    public clear() {
        super.clear();

        this.indicesDesc = null;
        this.nonIndexedPositionsDesc = null;
    }

    public getLoadDataQuery() {
        return {
            url: this.object.getModel().getBaseUrl() + 'fonts/' + this.properties.fontstyle.family,
            binSize: this.getBinaryDataSize()
        };
    }

    public loadData(aBuffer: Uint8Array) {
        let fontCache = this.object.getModel().getDocument().getFontCache();

        fontCache.addFontToCache(aBuffer, this.properties.fontstyle);

        let fontCacheItem = fontCache.getFontCacheItem(this.properties.fontstyle);

        if (!fontCacheItem.fontObject) {
            return false;
        }

        let transformationMat = new Float32Array(this.properties.transformation);
        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            mat4.multiply(transformationMat, repetitionTrafo, transformationMat);
        }

        let data: any = {
            positions: [],
            normals: [],
            indices: []
        };

        // compute normal
        let rotationMatrix = mat3.create();
        mat3.fromMat4(rotationMatrix, transformationMat);
        let n = vec3.create();
        vec3.transformMat3(n, vec3.fromValues(0, 0, -1), rotationMatrix);

        fontCacheItem.fontObject.forEachGlyph(this.properties.string, 0, 0, this.properties.fontstyle.size, null, function (glyph, x, y, fontSize) {
            let path = glyph.getPath(x, y, fontSize);
            let shapes = decompose(path, {steps: 10});

            if (shapes.length) {
                let triangles = triangulate(shapes, null);

                let triangle;
                for (let j = 0; j < triangles.length; j++) {

                    triangle = triangles[j];

                    // V1
                    let v = vec3.create();
                    //vec3.transformMat4(v, vec3.fromValues(triangle.points_[0].x, 0, triangle.points_[0].y), transformationMat);
                    vec3.transformMat4(v, vec3.fromValues(triangle.points_[0].x, -triangle.points_[0].y, 0), transformationMat);
                    data.positions.push(v[0], v[1], v[2]);

                    // V2
                    v = vec3.create();
                    //vec3.transformMat4(v, vec3.fromValues(triangle.points_[1].x, 0, triangle.points_[1].y), transformationMat);
                    vec3.transformMat4(v, vec3.fromValues(triangle.points_[1].x, -triangle.points_[1].y, 0), transformationMat);
                    data.positions.push(v[0], v[1], v[2]);

                    //V3
                    v = vec3.create();
                    //vec3.transformMat4(v, vec3.fromValues(triangle.points_[2].x, 0, triangle.points_[2].y), transformationMat);
                    vec3.transformMat4(v, vec3.fromValues(triangle.points_[2].x, -triangle.points_[2].y, 0), transformationMat);
                    data.positions.push(v[0], v[1], v[2]);

                    data.normals.push(n[0], n[1], n[2]);
                    data.normals.push(n[0], n[1], n[2]);
                    data.normals.push(n[0], n[1], n[2]);

                    data.indices.push((data.positions.length / 3) - 3);
                    data.indices.push((data.positions.length / 3) - 2);
                    data.indices.push((data.positions.length / 3) - 1);
                }
            }
        });

        data.positions = new Float32Array(data.positions);
        data.normals = new Float32Array(data.normals);
        data.indices = new Uint32Array(data.indices);

        this.setDescriptors(data);

        data = null;
    }

    protected setDescriptors(aData: any) {
        // fill mesh with data
        let positionAttributeSize = this.object.getPositionAttributeSize();
        this.vertexCount = aData.positions.byteLength / (positionAttributeSize * 4);

        this.positionsDesc = new VertexDataDescriptor(
            "positions",
            positionAttributeSize,
            aData.positions.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            aData.positions
        );

        this.normalsDesc = new VertexDataDescriptor(
            "normals",
            3,
            aData.normals.length / 3,
            "FLOAT",
            4,
            0,
            aData.normals
        );

        this.indicesDesc = new VertexDataDescriptor(
            "indices",
            1,
            aData.indices.length,
            "UINT",
            4,
            0,
            aData.indices
        );

        this.numIndices = aData.indices.length;
        this.numFaces = aData.indices.length / this.object.getPrimitiveSize();

        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }

    public transform(aTransformation: mat4) {
        AbstractGeometry.prototype.transform.call(this, aTransformation);

        let positionAttributeSize = this.object.getPositionAttributeSize();
        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }
}

Factory.register(Text);
